import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Plus, Check, Radio, RefreshCw, Undo2 } from "lucide-react";

function MatchPicker({ matches, onPick, onRefresh, busy }) {
  return (
    <div className="space-y-2" data-testid="sk-match-list">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Active matches · tap one to score</div>
        <button data-testid="sk-refresh" onClick={onRefresh} className="text-zinc-500 hover:text-white p-1"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /></button>
      </div>
      {matches.length === 0 && <div className="border border-pb-border p-6 text-center text-zinc-500 text-sm" data-testid="sk-empty">No matches on court right now. Pull to refresh once the TD assigns courts.</div>}
      {matches.map((m) => (
        <button key={`${m.tid}-${m.key}`} data-testid={`sk-match-${m.key}`} onClick={() => onPick(m)}
          className={`w-full text-left border p-3 active:bg-pb-elevated transition-colors ${m.main_court ? "border-red-500/60 bg-red-500/5" : "border-pb-border bg-pb-surface"}`}>
          <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <span>{m.court ? `Court ${m.court}` : "No court"} · {m.tournament}</span>
            {m.main_court && <span className="flex items-center gap-1 text-red-400 font-black"><Radio className="w-3 h-3 animate-pulse" /> Main Court</span>}
          </div>
          <div className="font-display font-bold uppercase text-sm mt-1 leading-tight">{m.side_a} <span className="text-zinc-600 font-mono text-xs">vs</span> {m.side_b}</div>
          <div className="flex items-center justify-between text-[10px] text-zinc-500 mt-1">
            <span>{m.label}{m.round ? ` · ${m.round}` : ""}</span>
            {m.live_score && <span className="font-mono text-amber-300">{m.live_score.a}–{m.live_score.b} · G{m.live_score.game}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

function ScorePad({ m, onBack, onDone }) {
  const [a, setA] = useState(m.live_score?.a ?? 0);
  const [b, setB] = useState(m.live_score?.b ?? 0);
  const [game, setGame] = useState(m.live_score?.game ?? 1);
  const [games, setGames] = useState(m.live_score?.games || []);
  const [history, setHistory] = useState([]);   // point log for Undo: {side} or {type:"game", a, b}
  const [busy, setBusy] = useState(false);
  const [synced, setSynced] = useState(true);
  const timer = useRef(null);
  const first = useRef(true);

  const push = useCallback((na, nb, ng, ngames) => {
    clearTimeout(timer.current);
    setSynced(false);
    timer.current = setTimeout(() => {
      api.put(`/scorekeeper/matches/${m.tid}/${m.key}/live`, { score_a: na, score_b: nb, game: ng, games: ngames })
        .then(() => setSynced(true))
        .catch(() => toast.error("Live score didn't sync — check connection"));
    }, 250);
  }, [m.tid, m.key]);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    push(a, b, game, games);
  }, [a, b, game, games, push]);

  const point = (side) => {
    if (side === "a") setA((v) => Math.min(99, v + 1)); else setB((v) => Math.min(99, v + 1));
    setHistory((h) => [...h, { side }]);
    if (navigator.vibrate) navigator.vibrate(10);
  };
  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return toast.message("Nothing to undo");
    if (last.type === "game") {
      setGames((g) => g.slice(0, -1)); setGame((g) => g - 1); setA(last.a); setB(last.b);
      toast.message(`Reopened game ${game - 1} at ${last.a}–${last.b}`);
    } else if (last.side === "a") setA((v) => Math.max(0, v - 1));
    else setB((v) => Math.max(0, v - 1));
    setHistory((h) => h.slice(0, -1));
    if (navigator.vibrate) navigator.vibrate([10, 30, 10]);
  };
  const nextGame = () => {
    if (a === b) return toast.error("Game can't end tied");
    setHistory((h) => [...h, { type: "game", a, b }]);
    setGames((g) => [...g, [a, b]]);
    setGame((g) => g + 1);
    setA(0); setB(0);
    toast.message(`Game ${game} logged ${a}–${b}`);
  };
  const complete = async () => {
    const all = a === 0 && b === 0 && games.length ? games : [...games, [a, b]];
    const wa = all.filter((g) => g[0] > g[1]).length, wb = all.filter((g) => g[1] > g[0]).length;
    const useGames = all.length > 1;
    const fa = useGames ? wa : all[0][0], fb = useGames ? wb : all[0][1];
    if (fa === fb) return toast.error("Match can't end tied — finish the game first");
    if (!window.confirm(`Match complete: ${m.side_a} ${fa} – ${fb} ${m.side_b}?\nThis locks the result and updates PSI.`)) return;
    setBusy(true);
    try {
      clearTimeout(timer.current);
      await api.post(`/scorekeeper/matches/${m.tid}/${m.key}/final`, { score_a: fa, score_b: fb, games: useGames ? all : null });
      toast.success("Match complete — PSI updated, scoreboard shows FINAL");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't complete the match"); }
    finally { setBusy(false); }
  };
  const lead = a === b ? null : a > b ? "a" : "b";

  return (
    <div className="space-y-3" data-testid="sk-scorepad">
      <div className="flex items-center gap-2">
        <button data-testid="sk-back" onClick={onBack} className="text-zinc-400 hover:text-white p-1"><ChevronLeft className="w-6 h-6" /></button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{m.court ? `Court ${m.court} · ` : ""}{m.tournament} · {m.label}</div>
          <div className="text-xs text-amber-300 font-mono">Game {game}{games.length ? ` · ${games.map((g) => `${g[0]}-${g[1]}`).join("  ")}` : ""}</div>
        </div>
        <span className={`flex items-center gap-1 text-[10px] font-black uppercase ${synced ? "text-emerald-400" : "text-amber-300"}`} data-testid="sk-sync">
          <span className={`h-2 w-2 rounded-full ${synced ? "bg-emerald-400" : "bg-amber-300 animate-pulse"}`} />{synced ? "Synced" : "Syncing"}
        </span>
        {m.main_court && <span className="flex items-center gap-1 text-red-400 text-[10px] font-black uppercase"><Radio className="w-3 h-3 animate-pulse" /> Live</span>}
      </div>

      {/* Live Scorer: one giant tap zone per side = +1 point */}
      <div className="grid grid-cols-2 gap-3" data-testid="sk-tap-zones">
        {[["a", m.side_a, a], ["b", m.side_b, b]].map(([side, name, val]) => (
          <button key={side} type="button" data-testid={`sk-point-${side}`} onClick={() => point(side)}
            className={`min-h-[42vh] sm:min-h-[300px] border-2 p-3 flex flex-col items-center justify-between select-none touch-manipulation active:scale-[0.97] transition-transform
              ${side === "a" ? "border-pb-cyan bg-pb-cyan/10 active:bg-pb-cyan/25" : "border-pb-red bg-pb-red/10 active:bg-pb-red/25"} ${lead === side ? "ring-2 ring-offset-2 ring-offset-black " + (side === "a" ? "ring-pb-cyan" : "ring-pb-red") : ""}`}>
            <div className="font-display font-black uppercase text-sm sm:text-lg leading-tight text-center line-clamp-2">{name}</div>
            <div className="font-mono font-black text-[22vw] sm:text-9xl leading-none tabular-nums" data-testid={`sk-score-${side}`}>{val}</div>
            <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${side === "a" ? "text-pb-cyan" : "text-pb-red"}`}><Plus className="w-3.5 h-3.5" /> Tap for point</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <button data-testid="sk-undo" onClick={undo} disabled={history.length === 0}
          className="h-14 border border-zinc-600 text-zinc-200 font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-40">
          <Undo2 className="w-4 h-4" /> Undo Point
        </button>
        <button data-testid="sk-next-game" onClick={nextGame}
          className="h-14 border border-amber-400/60 text-amber-300 font-black uppercase tracking-widest text-[11px] active:scale-95 transition-transform">Next Game →</button>
        <button data-testid="sk-finalize" onClick={complete} disabled={busy}
          className="h-14 bg-[#A3E635] text-black font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50">
          <Check className="w-4 h-4" /> Match Complete
        </button>
      </div>
      <p className="text-[10px] text-zinc-600 text-center">Every point broadcasts to the /live scoreboard and court boards within seconds. {history.length} point{history.length === 1 ? "" : "s"} logged this session.</p>
    </div>
  );
}

export default function Scorekeeper() {
  const [matches, setMatches] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/scorekeeper/matches"); setMatches(data || []); }
    catch (e) { toast.error(e?.response?.status === 403 ? "Scorekeeper access required" : "Couldn't load matches"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); const iv = setInterval(() => { if (!sel) load(); }, 15000); return () => clearInterval(iv); }, [load, sel]);

  return (
    <div className="max-w-lg mx-auto" data-testid="scorekeeper-page">
      <div className="mb-4">
        <h1 className="font-display font-black uppercase text-2xl sm:text-3xl leading-none">Score Entry</h1>
        <p className="text-xs text-zinc-500">Scorekeeper mode · fast entry, phone-friendly</p>
      </div>
      {sel ? <ScorePad m={sel} onBack={() => { setSel(null); load(); }} onDone={() => { setSel(null); load(); }} />
        : <MatchPicker matches={matches} onPick={setSel} onRefresh={load} busy={busy} />}
    </div>
  );
}
